import MenuIcon from '@mui/icons-material/Menu';
import AppBar, { AppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { Link } from 'react-router-dom';
import dmdGoldLockup from '../../assets/dmd/dmd-gold-fa.svg';
import {
  ALLIANCE_MOBILE_BREAKPOINT,
  ALLIANCE_THRESHOLDS,
  APP_BAR,
  DMD_BRAND,
} from './alliance-tokens';
import { AppLauncher } from './app-launcher';
import { BarButton, BarOffsetContext, useIsMobileLayout } from './bar-controls';
import { CURRENT_APP, CURRENT_APP_FONTS } from './current-app';

/**
 * Returns `value` only after it has stayed true for `delay` ms, so fast
 * loads never flash a progress bar.
 */
function useDelayedFlag(params: { value: boolean; delay: number }) {
  // Params
  const { value, delay } = params;
  // State
  const [delayed, setDelayed] = React.useState(false);

  React.useEffect(() => {
    if (!value) {
      setDelayed(false);
      return;
    }
    const timer = window.setTimeout(() => setDelayed(true), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return delayed;
}

/** App name next to the logo, in the app's nav/brand font */
const BRAND_TEXT = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase' as const,
  ...((CURRENT_APP_FONTS.nav as React.CSSProperties | null) || {}),
};

/**
 * The compact gold DMD lockup, optionally followed by a divider and the app
 * name. The lockup is an <img> because its gradients use fixed ids that
 * would collide if two inline copies shared a page. Never put a blue
 * lockup on navy.
 *
 * Used in the phone App Bar (20px, no name) and the sidebar / drawer header
 * (24px, with the name on desktop).
 *
 * @param compact - Phone sizing: tighter gap
 * @param showName - Show the divider and app name (default true)
 * @param homeTo - Make the brand a link to this route
 * @param onClick - Called when the brand link is followed (e.g. close a drawer)
 */
export function AllianceBrand(props: {
  compact: boolean;
  showName?: boolean;
  homeTo?: string;
  onClick?: () => void;
}) {
  const showName = props.showName !== false;
  const linkProps = props.homeTo
    ? {
        component: Link,
        to: props.homeTo,
        onClick: props.onClick,
        'aria-label': `${CURRENT_APP.fullName} home`,
      }
    : {};
  return (
    <Box
      {...linkProps}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: props.compact ? 1 : '12px',
        px: '4px',
        flex: 'none',
        color: '#ffffff',
        textDecoration: 'none',
        borderRadius: '6px',
        '&:focus-visible': {
          outline: '2px solid #ffffff',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="img"
        src={dmdGoldLockup}
        alt="DMD"
        sx={{
          display: 'block',
          height: 32,
          flex: 'none',
        }}
      />
      {showName ? (
        <React.Fragment>
          <Box
            sx={{ width: '1px', height: 24, backgroundColor: '#ffffff40' }}
          />
          <Box
            sx={{
              ...BRAND_TEXT,
              fontSize: props.compact ? 11 : 13,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {CURRENT_APP.name}
          </Box>
        </React.Fragment>
      ) : null}
    </Box>
  );
}

/**
 * The unified App Bar. Fixed order, left to right: menu button (below md),
 * page title (sidebar apps on desktop) or brand, top nav, actions slot,
 * divider, user control, App Launcher.
 *
 * 64px tall on desktop, 56px below md, always the app's bar color.
 *
 * @param title - Page title, shown at md+ in sidebar apps
 * @param homeTo - Brand links to this route (top-nav apps: their Home page)
 * @param nav - Top-nav links (top-nav apps, desktop only)
 * @param actions - The actions slot (cart, bell, Getting started)
 * @param user - A UserControl, or a Sign in button for anonymous visitors
 * @param launcher - Show the App Launcher (default: when `user` is set)
 * @param navBreakpoint - Top-nav apps: below this width (px) the nav links
 *   fold into the drawer behind the menu button, even above md, so a long
 *   link set never runs under the user control. The rest of the phone layout
 *   still switches at md. Default md
 * @param onMenuClick - Opens the nav drawer below md (or navBreakpoint)
 * @param onMenuClose - Closes the nav drawer; called if the window widens
 *   past navBreakpoint while it's open
 * @param menuOpen - The nav drawer is open (menu button fill)
 * @param sidebarWidth - Sidebar apps: the bar starts right of the permanent
 *   drawer at md+
 * @param loading - Page-to-page loading: a 3px bar under the bar after 150ms
 * @param position - AppBar position (default fixed)
 * @param topOffset - A fixed bar starts this many px below the top of the
 *   window (Dashboard: the ad banner carousel above it). Menus opened from
 *   the bar move down with it
 */
export function AllianceAppBar(props: {
  title?: string;
  homeTo?: string;
  nav?: React.ReactNode;
  actions?: React.ReactNode;
  user?: React.ReactNode;
  launcher?: boolean;
  navBreakpoint?: number;
  onMenuClick?: () => void;
  onMenuClose?: () => void;
  menuOpen?: boolean;
  sidebarWidth?: number;
  loading?: boolean;
  position?: AppBarProps['position'];
  topOffset?: number;
}) {
  // Props
  const { menuOpen, onMenuClose } = props;
  // Hooks
  const theme = useTheme();
  const isMobile = useIsMobileLayout();
  const navCollapsed = useMediaQuery(
    theme.breakpoints.down(props.navBreakpoint ?? ALLIANCE_MOBILE_BREAKPOINT),
  );
  const showProgress = useDelayedFlag({
    value: !!props.loading,
    delay: ALLIANCE_THRESHOLDS.pageLoadingDelayMs,
  });

  const hasSidebar = props.sidebarWidth !== undefined;
  const showTitle = hasSidebar && !isMobile && props.title;
  const showBrand = !hasSidebar || isMobile;
  const showLauncher = props.launcher ?? props.user !== undefined;
  const topOffset = props.topOffset ?? 0;

  React.useEffect(() => {
    // The nav links are back in the bar: the drawer has nothing to add
    if (!navCollapsed && menuOpen) {
      onMenuClose?.();
    }
  }, [navCollapsed, menuOpen, onMenuClose]);

  return (
    <AppBar
      component="header"
      elevation={0}
      position={props.position || 'fixed'}
      sx={{
        // MUI only sets a z-index for fixed/sticky bars; a relative bar (ARC)
        // still has to sit above the page header so the loading bar shows
        zIndex: (theme) => theme.zIndex.appBar,
        top: topOffset,
        backgroundColor: CURRENT_APP.bar,
        color: '#ffffff',
        letterSpacing: 'normal',
        backgroundImage: 'none',
        width: hasSidebar
          ? { md: `calc(100% - ${props.sidebarWidth}px)` }
          : undefined,
        ml: hasSidebar ? { md: `${props.sidebarWidth}px` } : undefined,
      }}
    >
      <Box
        sx={{
          height: { xs: APP_BAR.heightMobile, md: APP_BAR.heightDesktop },
          display: 'flex',
          alignItems: 'center',
          gap: `${APP_BAR.gap}px`,
          px: {
            xs: `${APP_BAR.paddingMobile}px`,
            md: `${APP_BAR.paddingDesktop}px`,
          },
        }}
      >
        {navCollapsed && props.onMenuClick ? (
          <BarButton
            square
            open={props.menuOpen}
            aria-label="Open navigation"
            aria-expanded={props.menuOpen ? 'true' : 'false'}
            onClick={props.onMenuClick}
          >
            <MenuIcon sx={{ fontSize: 22 }} />
          </BarButton>
        ) : null}

        {showTitle ? (
          <Box
            component="h1"
            sx={{
              m: 0,
              pl: '12px',
              fontSize: 20,
              fontWeight: 500,
              lineHeight: 1.2,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props.title}
          </Box>
        ) : null}

        {showBrand ? (
          <AllianceBrand
            compact={isMobile}
            showName={!hasSidebar}
            homeTo={props.homeTo}
          />
        ) : null}

        {!navCollapsed && props.nav ? props.nav : null}

        <Box sx={{ flex: 1 }} />

        <BarOffsetContext.Provider value={topOffset}>
          {props.actions}

          {!isMobile && props.actions && props.user ? (
            <Box
              sx={{
                width: '1px',
                height: 24,
                backgroundColor: APP_BAR.slotDivider,
                mx: '6px',
                flex: 'none',
              }}
            />
          ) : null}

          {props.user}

          {showLauncher ? <AppLauncher /> : null}
        </BarOffsetContext.Provider>
      </Box>

      {showProgress ? (
        <LinearProgress
          aria-label="Loading page"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -3,
            height: 3,
            backgroundColor: `${DMD_BRAND.progress}2e`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: DMD_BRAND.progress,
            },
          }}
        />
      ) : null}
    </AppBar>
  );
}
